import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiltersBar, Icon, InfiniteScrollingObserver, PeriodFilter, SearchInput, SingleSelect } from '~/components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { intervalOptions, intervalsByScope } from '~/utils';
import {
  EmptyState,
  EntityDetail,
  EntityIcon,
  EntityImage,
  EntityInfo,
  EntityName,
  EntityRow,
  Mono,
  Table,
  Td,
  Th,
} from '../ai/replay/shared';
import EntitySearchFilter from '../ai/replay/EntitySearchFilter';
import WorkspaceDisplay from '../ai/replay/WorkspaceDisplay';
import ActorCell from '../traces/ActorCell';

const BuildingIcon = styled(Icon).attrs({ icon: 'building' })`
  font-size: 1rem;
  color: ${colors.grey25};
`;

const BuildingIconSmall = styled(Icon).attrs({ icon: 'building' })`
  font-size: 0.875rem;
  color: ${colors.grey25};
`;

const SelectedLabel = styled.span`
  margin-left: 0.375rem;
`;

// Standard filter bar (slim 2rem controls) pinned to four equal columns, mirroring
// the Trace list: Search (two slots) · Workspace · Type / Last changed. `flex: none`
// resets FiltersBar's `flex: 1` (meant for a flex-row Page.Filters) so it sits at
// content height in the admin Page's flex column rather than stretching to fill it.
const Filters = styled(FiltersBar)`
  grid-template-columns: repeat(4, minmax(0, 1fr));
  flex: none;
  align-content: start;
  margin-bottom: 1.5rem;
`;

const SearchSpan = styled.div`
  grid-column: span 2;
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover td {
    background: ${colors.grey5};
  }
`;

const TypeLink = styled(Link)`
  font-size: 0.8125rem;
  font-weight: ${weights.bold};
  color: ${colors.black};

  &:hover {
    color: ${colors.primary};
  }
`;

const Source = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const Count = styled.span`
  font-variant-numeric: tabular-nums;
`;

function renderWorkspaceItem(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.5rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIcon />}
      </EntityIcon>
      <EntityInfo>
        <EntityName>{item.label}</EntityName>
        {item.key && <EntityDetail>{item.key}</EntityDetail>}
      </EntityInfo>
    </EntityRow>
  );
}

function renderWorkspaceSelected(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.125rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIconSmall />}
      </EntityIcon>
      <SelectedLabel>{item.label}</SelectedLabel>
    </EntityRow>
  );
}

export default function EntityList() {
  const api = useApi();
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const [entityType, setEntityType] = useState(urlParams.get('entity') || '');
  const [searchText, setSearchText] = useState(urlParams.get('q') || '');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [period, setPeriod] = useState(() => {
    const start = urlParams.get('start') || undefined;
    const end = urlParams.get('end') || undefined;
    return start || end ? { key: intervalOptions.custom.key, start, end } : intervalOptions.all_dates;
  });
  const start = period.start || undefined;
  const end = period.end || undefined;

  // Resolve workspace from the URL param on mount.
  const initialWorkspaceId = useRef(urlParams.get('workspaceId'));

  useEffect(() => {
    const wId = initialWorkspaceId.current;
    if (!wId) return;

    api.admin.traceReplay.resolve({ workspaceId: wId }).then(({ data }) => {
      if (data.workspace) {
        setSelectedWorkspace({
          id: data.workspace.id,
          label: data.workspace.name,
          key: data.workspace.key,
          imageUrl: data.workspace.imageUrl,
        });
      }
    });
  }, [api]);

  // Sync filters to URL.
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedWorkspace) params.set('workspaceId', selectedWorkspace.id);
    if (entityType) params.set('entity', entityType);
    if (searchText) params.set('q', searchText);
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    history.replace({ search: qs ? `?${qs}` : '' });
  }, [selectedWorkspace, entityType, searchText, start, end, history]);

  const searchDebounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(urlParams.get('q') || '');

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchText]);

  const loadMore = useCallback(async () => {
    const params = { from: entities.length };
    if (entityType) params.entity = entityType;
    if (debouncedSearch) params.q = debouncedSearch;
    if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
    if (start) params.start = start;
    if (end) params.end = end;

    const { data } = await api.admin.entityReplay.list(params);
    setEntities((prev) => [...prev, ...data.entities]);
    setHasMore(data.hasMore);
  }, [api, entityType, debouncedSearch, selectedWorkspace, start, end, entities.length]);

  useEffect(() => {
    let cancelled = false;
    setEntities([]);
    setHasMore(false);
    setIsLoading(true);

    const fetch = async () => {
      try {
        const params = {};
        if (entityType) params.entity = entityType;
        if (debouncedSearch) params.q = debouncedSearch;
        if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
        if (start) params.start = start;
        if (end) params.end = end;

        const { data } = await api.admin.entityReplay.list(params);
        if (!cancelled) {
          setEntities(data.entities);
          setEntityTypes(data.entityTypes);
          setHasMore(data.hasMore);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [api, entityType, debouncedSearch, selectedWorkspace, start, end]);

  const searchWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (text) params.q = text;
      if (from) params.from = from;
      const { data } = await api.admin.traceReplay.searchWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  const openWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (from) params.from = from;
      const { data } = await api.admin.traceReplay.recentWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  return (
    <>
      <Filters>
        <SearchSpan>
          <SearchInput
            value={searchText}
            placeholder="Search id or type"
            onChange={({ target }) => setSearchText(target.value)}
          />
        </SearchSpan>
        <EntitySearchFilter
          placeholder="Workspace"
          selected={selectedWorkspace}
          onSelect={setSelectedWorkspace}
          onClear={() => setSelectedWorkspace(null)}
          searchFn={searchWorkspaces}
          onOpen={openWorkspaces}
          renderItem={renderWorkspaceItem}
          renderSelected={renderWorkspaceSelected}
        />
        <SingleSelect
          placeholder="Type"
          value={entityType}
          showEmptyOption
          onChange={(e) => setEntityType(e.target.value)}>
          {entityTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </SingleSelect>
        <PeriodFilter
          name="period"
          placeholder="Last changed"
          scope="day"
          intervals={[intervalOptions.all_dates, ...intervalsByScope.day]}
          value={period}
          onChange={({ target: { value } }) => setPeriod(value)}
        />
      </Filters>

      {isLoading && <EmptyState>Loading entities...</EmptyState>}

      {!isLoading && entities.length === 0 && <EmptyState>No entities found.</EmptyState>}

      {!isLoading && entities.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Entity</Th>
                <Th>Workspace</Th>
                <Th>Last actor</Th>
                <Th>Traces</Th>
                <Th>Last action</Th>
                <Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {entities.map((e) => (
                <ClickableRow
                  key={`${e.entity}:${e.entityId}`}
                  onClick={() => history.push(`/admin/entities/${e.entity}/${e.entityId}`)}>
                  <Td>
                    <TypeLink
                      to={`/admin/entities/${e.entity}/${e.entityId}`}
                      onClick={(event) => event.stopPropagation()}>
                      {e.entity}
                    </TypeLink>
                    <div>
                      <Mono>{e.entityId}</Mono>
                    </div>
                  </Td>
                  <Td>
                    <WorkspaceDisplay workspace={e.workspace} />
                  </Td>
                  <Td>
                    <ActorCell
                      actor={e.lastActor}
                      member={e.lastActorMember}
                      apiKeyId={e.lastApiKeyId}
                      admin={e.lastAdmin}
                      size="1.5rem"
                    />
                  </Td>
                  <Td>
                    <Count>{e.traceCount}</Count>
                  </Td>
                  <Td>
                    <Source>{e.lastSource || '-'}</Source>
                  </Td>
                  <Td title={e.lastSeen}>{moment(e.lastSeen).fromNow()}</Td>
                </ClickableRow>
              ))}
            </tbody>
          </Table>

          {hasMore && <InfiniteScrollingObserver key={entities.length} onIntersecting={loadMore} />}
        </>
      )}
    </>
  );
}
